'use client';

import { type Component, type ComponentProps } from '../../types/component';
import { tv } from '../../utils/tv';
import { useMenuItemContext } from './internal/MenuItemContext';
import { useMenuSelectionContext } from './internal/MenuSelectionContext';

const menuItemIndicatorClasses = {
  root: 'zds-menu-item-indicator-root',
} as const;

const menuItemIndicatorStyles = tv({
  base: [
    menuItemIndicatorClasses.root,
    'flex items-center first:mr-2 last:ml-2',
    'text-accent-secondary-main',
    '[&_svg]:h-5 [&_svg]:w-auto',
  ],
  variants: {
    isDisabled: {
      true: 'disabled-foreground',
    },
  },
});

interface MenuItemIndicatorBaseProps {
  /**
   * Render function that receives the selection state and returns the visual.
   */
  children: (state: { isSelected: boolean }) => React.ReactNode;
}

type MenuItemIndicatorProps = ComponentProps<
  MenuItemIndicatorBaseProps,
  'span'
>;

type MenuItemIndicatorComponent = Component<MenuItemIndicatorBaseProps, 'span'>;

const MenuItemIndicator: MenuItemIndicatorComponent = (props) => {
  const { children, className, ref, ...rest } = props;

  const { isSelected } = useMenuSelectionContext();
  const itemContext = useMenuItemContext();
  const isDisabled = itemContext?.isDisabled ?? false;

  return (
    <span
      {...rest}
      ref={ref}
      aria-hidden="true"
      className={menuItemIndicatorStyles({ isDisabled, className })}
    >
      {children({ isSelected })}
    </span>
  );
};

MenuItemIndicator.displayName = 'Menu.ItemIndicator';

export {
  MenuItemIndicator,
  type MenuItemIndicatorProps,
  type MenuItemIndicatorBaseProps,
  menuItemIndicatorClasses,
};
